import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useApi, useAuth } from "@ncp/api-client";

// Only these product areas and control categories leave the browser. Never read text, input values, URLs, or query strings.
export function activityArea(path: string): string {
  if (path === "/") return "home";
  if (path.startsWith("/admin/users")) return "accounts";
  if (path.startsWith("/admin/sessions")) return "sessions";
  if (path.startsWith("/admin")) return "admin";
  for (const [prefix, area] of [["/chat", "chat"], ["/agent", "chat"], ["/knowledge", "knowledge"], ["/assignments", "assignments"], ["/countries", "countries"], ["/studies", "studies"], ["/trust", "trust"], ["/waitlist", "waitlist"], ["/workspace", "workspace"]])
    if (path.startsWith(prefix!)) return area!;
  return "other";
}

type Event = { eventId: string; kind: "click" | "navigation" | "submit"; area: string; control: string };

export function SessionActivityTracker({ areaOverride }: { areaOverride?: string } = {}) {
  const api = useApi();
  const { session, controller } = useAuth();
  const { pathname } = useLocation();
  const sid = session?.sessionId;
  const area = useRef(areaOverride ?? activityArea(pathname));
  const enqueue = useRef<(kind: Event["kind"], control: string) => void>(() => {});

  useEffect(() => {
    if (!sid) return;
    let queue: Event[] = [];
    let sending = false;
    let stopped = false;
    enqueue.current = (kind, control) => {
      if (controller.read()?.sessionId !== sid) return;
      if (queue.length < 200) queue.push({ eventId: crypto.randomUUID(), kind, area: area.current, control });
    };
    const flush = async () => {
      if (sending || !queue.length || controller.read()?.sessionId !== sid) return;
      sending = true;
      const batch = queue.splice(0, 40);
      try { await api.post("/api/auth/activity", { sessionId: sid, events: batch }); }
      catch { if (!stopped && controller.read()?.sessionId === sid) queue = [...batch, ...queue].slice(0, 200); }
      finally { sending = false; }
    };
    const click = (e: MouseEvent) => {
      if (!e.isTrusted || !(e.target instanceof Element)) return;
      const target = e.target.closest("button,a,[role=button],[role=tab],[role=menuitem],input,select,textarea,summary");
      if (!target) { enqueue.current("click", "other"); return; }
      const role = target.getAttribute("role");
      const control = role === "tab" ? "tab" : role === "menuitem" ? "menu" : target.tagName === "A" ? "link"
        : ["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName) ? "field" : "button";
      enqueue.current("click", control);
    };
    const submit = (e: SubmitEvent) => { if (e.isTrusted) enqueue.current("submit", "form"); };
    const visibility = () => { if (document.visibilityState === "hidden") void flush(); };
    const timer = window.setInterval(() => void flush(), 5000);
    document.addEventListener("click", click, true);
    document.addEventListener("submit", submit, true);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      stopped = true;
      void flush();
      enqueue.current = () => {};
      clearInterval(timer);
      document.removeEventListener("click", click, true);
      document.removeEventListener("submit", submit, true);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [api, controller, sid]);

  useEffect(() => { area.current = areaOverride ?? activityArea(pathname); enqueue.current("navigation", "page"); }, [pathname, sid, areaOverride]);
  return null;
}
